import { Award, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SymbolChip } from "@/components/dental/SymbolChip";

const ACCENT = "#0d9488"; // teal — dental module accent

/* grading / debrief panel */
export function ResultPanel({ result, onRetry, onCases }) {
  const gradeColor =
    result.grade === "A" ? "#0d9488" : result.grade === "B" ? "#16a34a" : result.grade === "C" ? "#ca8a04" : result.grade === "D" ? "#ea580c" : "#dc2626";
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="dental-result">
      <div className="p-5 text-white" style={{ background: gradeColor }}>
        <div className="flex items-center gap-3">
          <Award className="w-8 h-8" />
          <div>
            <div className="text-3xl font-bold leading-none" data-testid="dental-grade">{result.grade}</div>
            <div className="text-sm opacity-90">{result.pct}% · overall accuracy</div>
            {result.perioPct != null && (
              <div className="text-[11px] opacity-90" data-testid="dental-breakdown">
                AVDC {result.avdcPct}% · Perio {result.perioPct}%
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div className="bg-white/15 rounded-md py-1.5">
            <div className="text-lg font-bold leading-none">{result.correct}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-90">Correct</div>
          </div>
          <div className="bg-white/15 rounded-md py-1.5">
            <div className="text-lg font-bold leading-none">{result.missed}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-90">Missed</div>
          </div>
          <div className="bg-white/15 rounded-md py-1.5">
            <div className="text-lg font-bold leading-none">{result.incorrect.length}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-90">Incorrect</div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Findings review</div>
        <ul className="space-y-2">
          {result.findingsResult.map((f, i) => (
            <li key={`${f.tooth}-${i}`} className="flex items-start gap-2 text-[13px]" data-testid={`result-finding-${i}`}>
              {f.satisfied ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${f.partial ? "text-amber-500" : "text-rose-500"}`} />
              )}
              <span className={f.satisfied ? "text-slate-500" : "text-slate-700"}>
                <span className="font-mono font-semibold text-slate-800">{f.tooth}</span>{" "}
                {f.symbols.map((s) => (
                  <SymbolChip key={s} code={s} />
                ))}{" "}
                — {f.note}
                {f.partial && <span className="text-amber-600 text-[11px] block">Partially charted ({f.got.join(", ")}).</span>}
              </span>
            </li>
          ))}
        </ul>

        {result.perioResult.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold mt-4 mb-2">
              Periodontal exam review
            </div>
            <ul className="space-y-2.5">
              {result.perioResult.map((p, i) => (
                <li key={`${p.tooth}-perio-${i}`} data-testid={`result-perio-${i}`}>
                  <div className="flex items-center gap-2 text-[13px]">
                    {p.satisfied ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <XCircle className={`w-4 h-4 shrink-0 ${p.partial ? "text-amber-500" : "text-rose-500"}`} />
                    )}
                    <span className="font-mono font-semibold text-slate-800">{p.tooth}</span>
                    <span className="text-[11px] text-slate-400">{p.correctCount}/{p.fields.length} indices</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1 ml-6">
                    {p.fields.map((f) => (
                      <span
                        key={f.key}
                        className={`text-[10px] rounded px-1.5 py-0.5 border font-mono ${
                          f.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"
                        }`}
                      >
                        {f.label}: {f.val ?? "—"}
                        {!f.ok && <span className="font-semibold"> → {f.exp}</span>}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {result.incorrect.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mt-4 mb-2">
              Incorrect / extra marks
            </div>
            <div className="flex flex-wrap gap-1.5">
              {result.incorrect.map((m, i) => (
                <span key={`${m.tooth}-${m.code}-${i}`} className="text-[11px] bg-rose-50 border border-rose-200 text-rose-700 rounded px-1.5 py-0.5 font-mono">
                  {m.tooth} · {m.code}
                </span>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-2 mt-5">
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="retry-case-btn" className="flex-1 text-xs">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Try again
          </Button>
          <Button size="sm" onClick={onCases} data-testid="result-cases-btn" className="flex-1 text-xs text-white" style={{ background: ACCENT }}>
            Choose case
          </Button>
        </div>
      </div>
    </div>
  );
}
